import React, { useMemo, useState } from 'react';
import { Card, Space, Typography, Form, Input, Button, Upload, Image, message, List, Tag, Divider, Modal } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useQuery } from 'react-query';
import { useAuthStore } from '../../stores/authStore';

const { Title, Text } = Typography;

/**
 * 展馆打卡页面组件（桌面版）
 * 主要功能：
 * - 发布展馆打卡：填写展馆名称，上传现场照片，并为每张照片设置关键词
 * - 列表展示：按时间倒序展示已发布的展馆打卡内容，照片下方显示关键词
 * - 图片上传：走论坛图片上传接口，后端会进行压缩
 */
const ExhibitionCheckin = () => {
  const { token, user } = useAuthStore();
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  // 保存已上传照片的元数据：{ url: string, keywords: string[] }
  const [photoMetas, setPhotoMetas] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [editPhotoMetas, setEditPhotoMetas] = useState([]);
  const [editFileList, setEditFileList] = useState([]);
  const [searchText, setSearchText] = useState('');

  /**
   * 拉取展馆打卡帖子列表
   * 分类固定为 exhibition-checkin
   */
  const { data: postData, refetch } = useQuery(['forumPosts', 'exhibition-checkin'], async () => {
    const res = await axios.get('/forum/posts', { params: { category: 'exhibition-checkin' } });
    return res.data;
  });

  // 计算过滤后的帖子列表（函数级注释：根据搜索框内容按展馆名称进行本地过滤）
  const filteredPosts = useMemo(() => {
    const list = postData?.posts || [];
    if (!searchText.trim()) return list;
    return list.filter(p => (p.title || '').includes(searchText.trim()));
  }, [postData, searchText]);

  /**
   * 处理上传变化：成功后把返回的 url 写入 photoMetas
   */
  const onUploadChange = ({ file, fileList: newList }) => {
    setFileList(newList);
    if (file.status === 'done') {
      const url = file.response?.url;
      if (url) {
        setPhotoMetas(prev => [...prev, { url, keywords: [] }]);
      }
      message.success('图片上传成功');
    } else if (file.status === 'error') {
      const errMsg = file.response?.message || '图片上传失败';
      message.error(errMsg);
    }
  };

  /**
   * 更新某张照片的关键词（以逗号或空格分隔，最终转换为数组）
   */
  const updatePhotoKeywords = (index, raw) => {
    const normalized = (raw || '')
      .split(/[，,\s]+/)
      .map(s => s.trim())
      .filter(Boolean);
    setPhotoMetas(prev => prev.map((m, i) => (i === index ? { ...m, keywords: normalized } : m)));
  };

  /**
   * 函数：updateEditPhotoKeywords
   * 作用：更新编辑模式下某张照片关键词（以逗号或空格分隔，最终转换为数组）
   */
  const updateEditPhotoKeywords = (index, raw) => {
    const normalized = (raw || '')
      .split(/[，,\s]+/)
      .map(s => s.trim())
      .filter(Boolean);
    setEditPhotoMetas(prev => prev.map((m, i) => (i === index ? { ...m, keywords: normalized } : m)));
  };

  /**
   * 关闭发布弹窗并重置表单与上传状态
   */
  const handleCloseModal = () => {
    setIsModalOpen(false);
    form.resetFields();
    setFileList([]);
    setPhotoMetas([]);
  };

  /**
   * 提交发布展馆打卡
   * - title: 展馆名称
   * - content: 若未填写备注，则自动用所有关键词汇总或“现场打卡”占位
   * - imageUrls: 使用对象数组以便后端存储 { url, keywords }
   * - category: 固定为 exhibition-checkin
   */
  const handleCreatePost = async (values) => {
    try {
      const allKeywords = Array.from(new Set(photoMetas.flatMap(m => m.keywords || [])));
      const payload = {
        title: values.title,
        content: values.note?.trim() || (allKeywords.length ? `关键词：${allKeywords.join('、')}` : '现场打卡'),
        category: 'exhibition-checkin',
        imageUrls: photoMetas.map(m => ({ url: m.url, keywords: m.keywords || [] }))
      };
      const res = await axios.post('/forum/posts', payload);
      if (res.data?.success) {
        message.success('发布成功');
        handleCloseModal();
        refetch();
      } else {
        message.error(res.data?.message || '发布失败');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || '发布失败');
    }
  };

  /**
   * 函数：handleOpenEdit
   * 作用：点击展馆名称进入编辑模式，仅作者或管理员可编辑
   */
  const handleOpenEdit = (item) => {
    const canEdit = user?.role === 'admin' || Number(user?.id) === Number(item.createdBy);
    if (!canEdit) {
      return message.warning('仅作者或管理员可修改该打卡记录');
    }
    setEditingPost(item);
    // 初始化编辑表单与图片关键词
    editForm.setFieldsValue({ title: item.title, note: item.content });
    const initMetas = Array.isArray(item.imageUrls)
      ? item.imageUrls.map(img => (typeof img === 'string' ? { url: img, keywords: [] } : { url: img.url, keywords: img.keywords || [] }))
      : [];
    setEditPhotoMetas(initMetas);
    setEditFileList([]);
    setIsEditOpen(true);
  };

  /**
   * 函数：onEditUploadChange
   * 作用：编辑模式下新增图片上传成功后写入编辑照片元数据
   */
  const onEditUploadChange = ({ file, fileList: newList }) => {
    setEditFileList(newList);
    if (file.status === 'done') {
      const url = file.response?.url;
      if (url) {
        setEditPhotoMetas(prev => [...prev, { url, keywords: [] }]);
      }
      message.success('图片上传成功');
    } else if (file.status === 'error') {
      const errMsg = file.response?.message || '图片上传失败';
      message.error(errMsg);
    }
  };

  /**
   * 函数：handleUpdatePost
   * 作用：提交编辑后的展馆打卡记录（标题、备注、图片关键词）
   */
  const handleUpdatePost = async (values) => {
    if (!editingPost) return;
    try {
      const payload = {
        title: values.title,
        content: (values.note || '').trim() || editingPost.content || '',
        imageUrls: editPhotoMetas.map(m => ({ url: m.url, keywords: m.keywords || [] }))
      };
      const res = await axios.put(`/forum/posts/${editingPost.id}`, payload);
      if (res.data?.success) {
        message.success('修改成功');
        setIsEditOpen(false);
        setEditingPost(null);
        refetch();
      } else {
        message.error(res.data?.message || '修改失败');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || '修改失败');
    }
  };

  /**
   * 渲染列表中单个帖子项，显示展馆名称、发布时间、照片与关键词
   */
  const renderPostItem = (item) => {
    const imgs = Array.isArray(item.imageUrls) ? item.imageUrls : [];
    return (
      <List.Item key={item.id}>
        <List.Item.Meta
          title={<a onClick={() => handleOpenEdit(item)}>{item.title}</a>}
          description={<Text type="secondary">由 {item.creatorName || item.createdBy} 发布于 {new Date(item.createdAt).toLocaleString()}</Text>}
        />
        {imgs.length ? (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Image.PreviewGroup>
              <Space wrap>
                {imgs.map((img, idx) => {
                  const url = typeof img === 'string' ? img : img?.url;
                  const keys = typeof img === 'object' ? (img?.keywords || []) : [];
                  return (
                    <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Image src={url} width={180} height={130} style={{ objectFit: 'cover' }} />
                      {keys.length > 0 && (
                        <Space wrap style={{ marginTop: 6 }}>
                          {keys.map((k, i) => (
                            <Tag key={i} color="geekblue">{k}</Tag>
                          ))}
                        </Space>
                      )}
                    </div>
                  );
                })}
              </Space>
            </Image.PreviewGroup>
          </Space>
        ) : null}
        {item.content ? (
          <div style={{ marginTop: 12, whiteSpace: 'pre-wrap' }}>{item.content}</div>
        ) : null}
      </List.Item>
    );
  };

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={3} style={{ margin: 0 }}>展馆打卡</Title>
          <Space>
            <Button type="primary" onClick={() => setIsModalOpen(true)}>发帖</Button>
          </Space>
        </div>
        <Input.Search
          allowClear
          placeholder="搜索展馆名称"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={(v) => setSearchText(v)}
        />

        <Modal title="发布展馆打卡" open={isModalOpen} onCancel={handleCloseModal} footer={null} destroyOnClose>
          <Form form={form} layout="vertical" onFinish={handleCreatePost}>
            <Form.Item label="展馆名称" name="title" rules={[{ required: true, message: '请输入展馆名称' }]}> 
              <Input placeholder="请输入展馆名称" allowClear />
            </Form.Item>
            <Form.Item label="现场照片（自动压缩≤800KB/张）">
              <Upload
                listType="picture-card"
                multiple
                maxCount={12}
                fileList={fileList}
                onChange={onUploadChange}
                action="/api/forum/upload-image"
                name="image"
                headers={token ? { Authorization: `Bearer ${token}` } : {}}
                accept="image/*"
              >
                {fileList.length >= 12 ? null : (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>上传</div>
                  </div>
                )}
              </Upload>
              {photoMetas.length > 0 && (
                <div>
                  <Divider style={{ margin: '12px 0' }}>为每张照片设置关键词</Divider>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {photoMetas.map((m, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <Image src={m.url} width={120} height={90} style={{ objectFit: 'cover' }} />
                        <Input
                          allowClear
                          placeholder="请输入关键词，使用逗号或空格分隔"
                          onChange={(e) => updatePhotoKeywords(idx, e.target.value)}
                        />
                      </div>
                    ))}
                  </Space>
                </div>
              )}
            </Form.Item>
            <Form.Item label="补充说明（可选）" name="note"> 
              <Input.TextArea rows={3} placeholder="可以填写参观感受、亮点等" allowClear />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button onClick={handleCloseModal}>取消</Button>
                <Button type="primary" htmlType="submit">发布</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        <Card title="最新打卡" className="status-card">
          <List
            itemLayout="vertical"
            dataSource={filteredPosts}
            renderItem={renderPostItem}
          />
        </Card>

        <Modal title="编辑展馆打卡" open={isEditOpen} onCancel={() => setIsEditOpen(false)} footer={null} destroyOnClose>
          <Form form={editForm} layout="vertical" onFinish={handleUpdatePost}>
            <Form.Item label="展馆名称" name="title" rules={[{ required: true, message: '请输入展馆名称' }]}> 
              <Input placeholder="请输入展馆名称" allowClear />
            </Form.Item>
            <Form.Item label="新增照片（自动压缩≤800KB/张）">
              <Upload
                listType="picture-card"
                multiple
                maxCount={12}
                fileList={editFileList}
                onChange={onEditUploadChange}
                action="/api/forum/upload-image"
                name="image"
                headers={token ? { Authorization: `Bearer ${token}` } : {}}
                accept="image/*"
              >
                {editFileList.length >= 12 ? null : (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>上传</div>
                  </div>
                )}
              </Upload>
              {editPhotoMetas.length > 0 && (
                <div>
                  <Divider style={{ margin: '12px 0' }}>编辑照片关键词</Divider>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {editPhotoMetas.map((m, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <Image src={m.url} width={120} height={90} style={{ objectFit: 'cover' }} />
                        <Input
                          allowClear
                          placeholder="请输入关键词，使用逗号或空格分隔"
                          defaultValue={(m.keywords || []).join('、')}
                          onChange={(e) => updateEditPhotoKeywords(idx, e.target.value)}
                        />
                      </div>
                    ))}
                  </Space>
                </div>
              )}
            </Form.Item>
            <Form.Item label="补充说明（可选）" name="note"> 
              <Input.TextArea rows={3} placeholder="可以修改参观感受、亮点等" allowClear />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button onClick={() => setIsEditOpen(false)}>取消</Button>
                <Button type="primary" htmlType="submit">保存修改</Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Space>
    </div>
  );
};

export default ExhibitionCheckin;